import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import { ApiError, ApiResponse } from "@/api"
import { requestUser, requestUserToken, UserResponse } from "@/api/UserService"
import { Token } from "@/types/index"
import { CurrentUser, UserRole } from "@/types/user"
import { merge, remove } from "@/utils/lodash"

interface UserState extends CurrentUser {
   isSuperAdmin: boolean | null
   role: UserRole | null
   loading: boolean
   error: number | null
   selectedTenantId: string | null
   rtsToken: string | null
   rtsTokenError: number | null
}

export const initialState: UserState = {
   id: "",
   email: "",
   firstName: "",
   lastName: "",
   isSuperAdmin: null,
   role: null,
   tenants: [],
   loading: false,
   error: null,
   selectedTenantId: null,
   rtsToken: null,
   rtsTokenError: null,
}

export const getUser = createAsyncThunk("user/getUser", async (_, thunkAPI) => {
   try {
      const response: ApiResponse<UserResponse> = await requestUser()
      return response
   } catch (error) {
      if (error instanceof Error) {
         const apiError = error as unknown as ApiError
         return thunkAPI.rejectWithValue(apiError.status)
      }
      return thunkAPI.rejectWithValue(500)
   }
})

export const fetchRtsToken = createAsyncThunk(
   "user/fetchRtsToken",
   async (_, thunkAPI) => {
      try {
         const response = await requestUserToken()
         return response.data
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const userSlice = createSlice({
   name: "user",
   initialState,
   reducers: {
      addCustomer: (state, action) => {
         const customerData = action.payload
         state.tenants[0]?.customers.push(customerData)
      },

      addFacilityToCustomer: (state, action) => {
         const { customerId, facilityData } = action.payload
         const customer = state.tenants[0]?.customers.find(
            (c) => c.id === customerId
         )
         if (customer) {
            customer.facilities.push(facilityData)
         }
      },
      updateFacility: (state, action) => {
         const { facilityId, facilityData } = action.payload
         const customer = state.tenants[0]?.customers.find((c) =>
            c.facilities.some((f) => f.id === facilityId)
         )
         if (customer) {
            const facilityIndex = customer.facilities.findIndex(
               (f) => f.id === facilityId
            )
            if (facilityIndex !== -1) {
               customer.facilities[facilityIndex] = merge(
                  {},
                  customer.facilities[facilityIndex],
                  facilityData
               )
            }
         }
      },
      removeCustomer: (state, action) => {
         const customerId = action.payload
         remove(state.tenants[0].customers, (c) => c.id === customerId)
      },
      removeFacilityFromCustomer: (state, action) => {
         const { customerId, facilityId } = action.payload
         const customer = state.tenants[0]?.customers.find(
            (c) => c.id === customerId
         )
         if (customer) {
            remove(customer.facilities, (f) => f.id === facilityId)
         }
      },
      setSelectedTenant: (state, action) => {
         const tenantId = action.payload
         const selectedTenant = state.tenants.find(
            (tenant) => tenant.id === tenantId
         )
         if (selectedTenant) {
            state.selectedTenantId = selectedTenant.id
            sessionStorage.setItem(Token.TENANT_ID, tenantId)
            sessionStorage.removeItem(Token.CUSTOMER_ID)
            sessionStorage.removeItem(Token.FACILITY_ID)
            sessionStorage.removeItem(Token.FACILITY_LEVEL_SELECTORS)

            const isSuperAdmin = selectedTenant.role === UserRole.ADMIN
            state.isSuperAdmin = isSuperAdmin
         }
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(getUser.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            getUser.fulfilled,
            (state, action: PayloadAction<ApiResponse<UserResponse>>) => {
               const { id, email, firstName, lastName, tenants } =
                  action.payload.data
               const isSuperAdmin = tenants[0].role === UserRole.ADMIN
               const tenantId = tenants[0].id
               let role
               state.selectedTenantId = tenantId
               sessionStorage.setItem(Token.TENANT_ID, tenantId)
               if (!isSuperAdmin) {
                  sessionStorage.setItem(
                     Token.FACILITY_ID,
                     tenants[0].customers[0].facilities[0].id || ""
                  )
                  sessionStorage.setItem(
                     Token.CUSTOMER_ID,
                     tenants[0].customers[0].id || ""
                  )
                  role = tenants[0].customers[0].facilities[0].role
               }
               Object.assign(state, {
                  loading: false,
                  id,
                  email,
                  firstName,
                  lastName,
                  isSuperAdmin,
                  tenants,
                  role,
               })
            }
         )
         .addCase(getUser.rejected, (state) => {
            Object.assign(state, initialState)
         })
         .addCase(fetchRtsToken.pending, (state) => {
            state.rtsToken = null
            state.rtsTokenError = null
         })
         .addCase(
            fetchRtsToken.fulfilled,
            (state, action: PayloadAction<{ rtsToken: string }>) => {
               state.rtsToken = action.payload.rtsToken
               state.rtsTokenError = null
            }
         )
         .addCase(fetchRtsToken.rejected, (state, action) => {
            state.rtsToken = null
            state.rtsTokenError = (action.payload as number) ?? 500
         })
   },
})

export const {
   addCustomer,
   addFacilityToCustomer,
   updateFacility,
   removeCustomer,
   removeFacilityFromCustomer,
   setSelectedTenant,
} = userSlice.actions

export default userSlice.reducer
